import { handleChange } from '@helpers/handleChange';
import { StoreState } from '@interfaces/stores';
import { workspaceService } from '@services/domain-factories/workspace';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { initOnce } from '@utils/zustand/initOnce';
import { Category, SurveyResponse, Tag, WorkspaceForm } from '@vincerosoft/assessfluence-types';

type Actions = {
  getTaxonomies: (forms: WorkspaceForm[]) => Promise<{
    tags: Tag[];
    categories: Category[];
  }>;
  initWorkspace: () => Promise<void>;
  handleChange: <T extends boolean>(
    formId: string,
    change: T extends true ? string : SurveyResponse,
    isDelete?: T extends true ? T : never
  ) => void;
};

const initialValue: StoreState['workspace'] = {
  taxonomies: null,
  isInitialized: false,
  inFlightInit: null,
  loading: false,
  byFormId: {}
};

export const workspaceStore = createStoreWithSelector<StoreState['workspace'] & Actions>()(
  (set, get) => ({
    ...initialValue,

    getTaxonomies: async (forms) => {
      const storedTaxonomies = get().taxonomies;
      if (storedTaxonomies)
        return storedTaxonomies as NonNullable<StoreState['workspace']['taxonomies']>;

      const taxonomies = await workspaceService.getTagsAndCategories(forms);
      set({ taxonomies });
      return taxonomies;
    },

    initWorkspace: () =>
      initOnce(
        'isInitialized',
        set,
        get,
        () =>
          workspaceService.loadWorkspaceData((forms) =>
            set({ byFormId: forms.reduce((acc, form) => ({ ...acc, [form.id]: { form } }), {}) })
          ),
        (workspaceData) => set({ ...workspaceData, isInitialized: true })
      ),

    handleChange: (formId, change, isDelete) => {
      return set((state) => {
        return {
          [formId]: {
            ...(state.byFormId[formId] || {}),
            responses: handleChange<SurveyResponse>(
              state.byFormId[formId]?.responses || [],
              change,
              isDelete
            )
          }
        };
      });
    }
  })
);

export const useWorkspaceStore = createUseStore(workspaceStore);
export const getWorkspaceState = workspaceStore.getState;
